// HeroSection.jsx
import React, { useEffect, useState, useRef } from "react";
import {
	Box,
	Container,
	Stack,
	Button,
	IconButton,
	Typography,
	useTheme,
	useMediaQuery,
} from "@mui/material";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { NoEncryption } from "@mui/icons-material";

const COLORS = {
	bg: "#FCFBF8",
	brown: "#674934",
	brownDark: "#533A29",
	border: "#D9CFC7",
	placeholder: "#E9E4DE",
};

export default function HeroSection() {
	const [scrolled, setScrolled] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const videoRef = useRef(null);
	const navigate = useNavigate();

	const theme = useTheme();
	const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

	useEffect(() => {
		const handleScroll = () => setScrolled(window.scrollY > 80);
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	useEffect(() => {
		const prev = document.body.style.overflow;
		if (menuOpen) document.body.style.overflow = "hidden";
		else document.body.style.overflow = prev || "";
		return () => {
			document.body.style.overflow = prev || "";
		};
	}, [menuOpen]);

	const enterFullScreen = () => {
		const v = videoRef.current;
		if (!v) return;
		if (v.requestFullscreen) v.requestFullscreen();
		else if (v.webkitRequestFullscreen) v.webkitRequestFullscreen();
		else if (v.msRequestFullscreen) v.msRequestFullscreen();
	};

	const showHeaderLogo = scrolled;

	return (
		<Box
			sx={{
				// no forced 100vh anywhere now
				minHeight: { xs: "auto", lg: "auto", xl: "auto" },
				position: "relative",
				overflowX: "hidden",
				bgcolor: "transparent",
			}}
		>
			{/* ===== FIXED HEADER ===== */}
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					zIndex: 1200,
					transition:
						"background-color 0.4s ease, box-shadow 0.4s ease, padding 0.4s ease",
					bgcolor: scrolled ? COLORS.bg : "transparent",
					borderBottom: scrolled ? `1px solid ${COLORS.border}` : "none",
					boxShadow: "none",
				}}
			>
				<Container
					maxWidth="xl"
					sx={{
						px: { xs: 2, sm: 3, lg: 6 },
						pt: { xs: 1.5, lg: 3 },
						pb: { xs: 1.5, lg: 2 },
						transition: "padding 0.4s ease",
					}}
				>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
					>
						<Box sx={{ display: "flex", alignItems: "center" }}>
							{showHeaderLogo && (
								<Box
									component="img"
									src="/images/Saffron_House.png"
									alt="Saffron House"
									sx={{
										display: "block",
										height: { xs: 40, lg: 52 },
										width: "auto",
										transition: "height 0.3s ease",
									}}
								/>
							)}
						</Box>
						{isLgUp && scrolled && (
							<Stack direction="row" spacing={1.5}>
								<Button
									variant="outlined"
									onClick={() => navigate("/projects")}
									sx={{
										borderRadius: 999,
										borderColor: COLORS.brown,
										color: COLORS.brown,
										height: 52,
										px: 2.5,
										fontFamily: "'Poppins', sans-serif",
										fontSize: 24,
										fontWeight: 500,
										textTransform: "none",
										bgcolor: "transparent",
										"&:hover": {
											bgcolor: "#F5F1EC",
											borderColor: COLORS.brownDark,
										},
									}}
								>
									Projects
								</Button>
								<Button
									variant="contained"
									disableElevation
									endIcon={<ArrowOutwardIcon fontSize="small" />}
									onClick={() =>
										window.open("https://wa.me/917000758077", "_blank")
									}
									sx={{
										color: "#fff",
										bgcolor: COLORS.brown,
										borderColor: COLORS.brown,
										"&:hover": {
											bgcolor: COLORS.brownDark,
											borderColor: COLORS.brownDark,
										},
										borderRadius: 999,
										px: 2.5,
										height: 52,
										fontSize: 20,
										textTransform: "none",
										fontFamily: "'Poppins', sans-serif",
										fontWeight: 500,
									}}
								>
									Contact Us
								</Button>
							</Stack>
						)}{" "}
						{!isLgUp && (
							<IconButton
								onClick={() => setMenuOpen((prev) => !prev)}
								sx={{
									color: COLORS.brown,
									display: { xs: "inline-flex", lg: "none" },
								}}
								aria-label="open menu"
							>
								{menuOpen ? <CloseIcon /> : <MenuIcon />}
							</IconButton>
						)}
					</Stack>
				</Container>
			</Box>

			{/* ===== HERO CONTENT (single layout, animated) ===== */}
			<Box
				sx={{
					pt: { xs: 6, sm: 7, lg: 9 },
					transition: "padding-top 0.4s ease",
				}}
			>
				{/* Big logo + tagline + desktop buttons */}
				<Box
					sx={{
						position: "relative",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						textAlign: "center",
						mt: 0,
						/*mt: { xs: 3, sm: 3.5, lg: 4 },*/
					}}
				>
					{/* big center logo */}
					<Box
						component="img"
						src="/images/Saffron_House.png"
						alt="Saffron House"
						sx={{
							width: { xs: "90%", sm: "80%", lg: "60%" },
							maxWidth: 1500,
							height: "auto",
							display: "block",
							opacity: scrolled ? 0 : 1,
							transform: scrolled
								? "translateY(-10px) scale(0.96)"
								: "translateY(0) scale(1)",
							transition: "opacity 0.35s ease, transform 0.35s ease",
							pointerEvents: scrolled ? "none" : "auto",
						}}
					/>

					{/* desktop overlay buttons when NOT scrolled */}
					{isLgUp && (
						<Stack
							direction="row"
							spacing={1.5}
							sx={{
								display: "none",
								position: "absolute",
								top: { md: "-32%", lg: "-15%", xl: "-16%" },
								right: { md: "14%", lg: "14%", xl: "18%" },
								opacity: scrolled ? 0 : 1,
								transform: scrolled ? "translateY(-8px)" : "translateY(0px)",
								transition: "opacity 0.35s ease, transform 0.35s ease",
								pointerEvents: scrolled ? "none" : "auto",
							}}
						>
							<Button
								variant="outlined"
								onClick={() => navigate("/projects")}
								sx={{
									borderRadius: 999,
									borderColor: COLORS.brown,
									color: COLORS.brown,
									height: 52,
									px: 2.5,
									fontFamily: "'Poppins', sans-serif",
									fontSize: 16,
									fontWeight: 500,
									textTransform: "none",
									bgcolor: "#FDF9F6",
									"&:hover": {
										bgcolor: "#F5F1EC",
										borderColor: COLORS.brownDark,
									},
								}}
							>
								Projects
							</Button>
							<Button
								variant="contained"
								disableElevation
								onClick={() =>
									window.open("https://wa.me/917000758077", "_blank")
								}
								endIcon={<ArrowOutwardIcon fontSize="small" />}
								sx={{
									borderRadius: 999,
									bgcolor: COLORS.brown,
									color: "#fff",
									border: `1px solid ${COLORS.brown}`,
									height: 52,
									px: 2.5,
									fontFamily: "'Poppins', sans-serif",
									fontSize: 16,
									fontWeight: 500,
									textTransform: "none",
									"&:hover": {
										bgcolor: COLORS.brownDark,
										borderColor: COLORS.brownDark,
									},
								}}
							>
								Contact Us
							</Button>
						</Stack>
					)}

					{/* tagline */}
					<Typography
						sx={{
							mt: { xs: 1.5, sm: 2, lg: 2 },
							fontFamily: "'Lora', serif",
							fontStyle: "italic",
							fontSize: { xs: 16, sm: 20, lg: 24 },
							lineHeight: 1.4,
							color: COLORS.brown,
							opacity: scrolled ? 0 : 1,
							transform: scrolled ? "translateY(-6px)" : "translateY(0px)",
							transition: "opacity 0.35s ease, transform 0.35s ease",
							pointerEvents: scrolled ? "none" : "auto",
						}}
					>
						Bespoke Interior Design That Redefines Spaces
					</Typography>
				</Box>

				{/* HERO VIDEO – aligned with ProjectsSection */}
				<Container
					maxWidth={false}
					sx={{
						width: { xs: "100%", md: "95%" },
						maxWidth: 1500,
						mx: "auto",
						px: { xs: 1, md: 0 },
						mt: scrolled ? { xs: 2.5, sm: 3, lg: 4 } : { xs: 3, sm: 4, lg: 6 },
						transition: "margin-top 0.4s ease",
					}}
					onClick={enterFullScreen}
				>
					<Box
						sx={{
							position: "relative",
							width: "100%",
							borderRadius: 2,
							overflow: "hidden",
						}}
					>
						<Box
							component="video"
							ref={videoRef}
							autoPlay
							loop
							muted
							playsInline
							sx={{
								width: "100%",
								height: {
									xs: 260,
									sm: 320,
									md: 420,
									lg: 617,
								},
								objectFit: "cover",
								display: "block",
							}}
						>
							<source src="/images/hero-section-video.mp4" type="video/mp4" />
							Your browser does not support the video tag.
						</Box>
					</Box>
				</Container>
			</Box>

			{/* ===== MOBILE FULL-SCREEN MENU ===== */}
			{menuOpen && !isLgUp && (
				<Box
					sx={{
						position: "fixed",
						inset: 0,
						bgcolor: COLORS.brown,
						color: COLORS.bg,
						zIndex: 1400,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Container
						maxWidth="xl"
						disableGutters
						sx={{ px: 2.5, py: 3, width: "100%" }}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								mb: 4,
							}}
						>
							<Box
								component="img"
								src="/images/Saffron_House.png"
								alt="Saffron House"
								sx={{
									height: 32,
									width: "auto",
									display: "block",
									filter: "brightness(0) invert(1)",
								}}
							/>
							<IconButton
								onClick={() => setMenuOpen(false)}
								sx={{
									color: COLORS.bg,
									display: { xs: "inline-flex", lg: "none" },
								}}
								aria-label="close menu"
							>
								<CloseIcon sx={{ fontSize: 32 }} />
							</IconButton>
						</Box>

						<Stack spacing={2.5}>
							<Button
								variant="text"
								disableRipple
								onClick={() => {
									setMenuOpen(false);
									navigate("/projects");
								}}
								sx={{
									justifyContent: "flex-start",
									px: 0,
									minWidth: 0,
									textTransform: "none",
									fontFamily: "'Poppins', sans-serif",
									fontSize: 24,
									color: COLORS.bg,
									"&:hover": {
										bgcolor: "transparent",
										opacity: 0.8,
									},
								}}
							>
								Projects
							</Button>
							<Button
								variant="text"
								disableRipple
								endIcon={<ArrowOutwardIcon />}
								onClick={() => {
									setMenuOpen(false);
									window.open("https://wa.me/917000758077", "_blank");
								}}
								sx={{
									justifyContent: "flex-start",
									px: 0,
									minWidth: 0,
									textTransform: "none",
									fontFamily: "'Poppins', sans-serif",
									fontSize: 24,
									color: COLORS.bg,
									"& .MuiButton-endIcon": {
										ml: 1,
									},
									"& .MuiButton-endIcon svg": {
										fontSize: 32,
									},
									"&:hover": {
										bgcolor: "transparent",
										opacity: 0.8,
									},
								}}
							>
								Contact Us
							</Button>
						</Stack>
					</Container>
				</Box>
			)}
		</Box>
	);
}
